import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useCreatePersonalAccessToken } from '../hooks'
import type { CreatePatResponse } from '@/lib/api/features/personal-access-tokens'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

type ExpiryChoice = '7d' | '30d' | '90d' | '1y' | 'never'

const EXPIRY_DAYS: Record<Exclude<ExpiryChoice, 'never'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1y': 365,
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (response: CreatePatResponse) => void
}

export function CreateTokenDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation('settings')
  const create = useCreatePersonalAccessToken()

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t('integrationsPage.tokens.createDialog.nameRequired'))
      .max(100, t('integrationsPage.tokens.createDialog.nameTooLong')),
    expiry: z.enum(['7d', '30d', '90d', '1y', 'never']),
  })
  type FormData = z.infer<typeof schema>

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', expiry: '90d' },
  })

  useEffect(() => {
    if (open) form.reset({ name: '', expiry: '90d' })
  }, [open, form])

  const onSubmit = async (data: FormData) => {
    const expiresAt =
      data.expiry === 'never'
        ? null
        : new Date(
            Date.now() + EXPIRY_DAYS[data.expiry] * 24 * 60 * 60 * 1000,
          ).toISOString()

    try {
      const response = await create.mutateAsync({
        name: data.name.trim(),
        expiresAt,
      })
      onCreated(response)
    } catch {
      toast.error(t('integrationsPage.tokens.createDialog.createFailed'))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && create.isPending) return
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('integrationsPage.tokens.createDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('integrationsPage.tokens.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="pat-name">
              {t('integrationsPage.tokens.createDialog.nameLabel')}
            </Label>
            <Input
              id="pat-name"
              autoFocus
              placeholder={t(
                'integrationsPage.tokens.createDialog.namePlaceholder',
              )}
              maxLength={100}
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="text-destructive text-sm">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>
              {t('integrationsPage.tokens.createDialog.expiryLabel')}
            </Label>
            <RadioGroup
              value={form.watch('expiry')}
              onValueChange={(value) =>
                form.setValue('expiry', value as ExpiryChoice)
              }
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
              <ExpiryOption value="7d" label={t('integrationsPage.tokens.createDialog.expiry7d')} />
              <ExpiryOption value="30d" label={t('integrationsPage.tokens.createDialog.expiry30d')} />
              <ExpiryOption value="90d" label={t('integrationsPage.tokens.createDialog.expiry90d')} />
              <ExpiryOption value="1y" label={t('integrationsPage.tokens.createDialog.expiry1y')} />
              <ExpiryOption value="never" label={t('integrationsPage.tokens.createDialog.expiryNever')} />
            </RadioGroup>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('integrationsPage.tokens.createDialog.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="size-4 animate-spin mr-2" />}
              {t('integrationsPage.tokens.createDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ExpiryOption({ value, label }: { value: ExpiryChoice; label: string }) {
  return (
    <label
      htmlFor={`pat-expiry-${value}`}
      className="flex items-center gap-2 rounded border border-input px-3 py-2 cursor-pointer hover:bg-accent"
    >
      <RadioGroupItem id={`pat-expiry-${value}`} value={value} />
      <span className="text-sm">{label}</span>
    </label>
  )
}
